'use strict'
import { AccountFeatures, AppContext, Features } from "@lightspeed-invoicing/core-client/frontend"
import $ from 'jquery'
import { Base64 } from 'js-base64'
import { formatNumber, parseNumber } from 'libphonenumber-js'
import moment from 'moment-timezone'
import React from 'react'
import { FormattedMessage, defineMessages } from 'react-intl'
import { getCountryCallingCode, isSupportedCountry, isValidPhoneNumber } from 'react-phone-number-input'
import Integrations from "../integration/Integrations.jsx"
import { ExternalPaymentTypesMapping } from "../payment/externalpaymenttypesmapping.jsx"
import { PAYMENT_TYPE } from "../state/helpers/Payment"
import { INTEGRATION_KEY, PAYMENT_INTEGRATION_KEYS } from "../state/integration/Constants"
import {
    FeatureEcommerceConfiguration,
    FeatureInventorySync,
    FeaturePurchaseOrderTracking
} from "../state/integration/IntegrationFeatureKeys"
import { FeatureFullPaymentAndCheckoutOnly } from "../state/integration/IntegrationFeatureKeys.ts"
import { INVOICE_STATUS } from "../state/invoice/InvoiceStatus.ts"
import CurrencyCodes from "./Currencies.js"
import { countryCodeRegularExpressions } from "./Helpers"
const uuidv4 = require('uuid/v4')

const provinces = require("provinces")
const Countries = require("i18n-iso-countries")
Countries.registerLocale(require("i18n-iso-countries/langs/en.json"))
Countries.registerLocale(require("i18n-iso-countries/langs/fr.json"))

const CountryOverride = {
    "en": {
        "US": "United States",
    },
    "fr": {
        "US": "États-Unis",
    }
}

const ShopifyAppIntegrationKeys = [INTEGRATION_KEY.SHOPIFY, INTEGRATION_KEY.SHOPIFY_EXPERT_APP]
const ExternalSubscriptionKeys = [INTEGRATION_KEY.SHOPIFY, INTEGRATION_KEY.SHOPIFY_EXPERT_APP, INTEGRATION_KEY.VEND]
const StartUpGuideIntegrationKeys = [INTEGRATION_KEY.SHOPIFY, INTEGRATION_KEY.SHOPIFY_EXPERT_APP]

const GlobalCache = {}

const emailValidationRegex = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+([,;] ?[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+)*$/
const phoneNumberValidationRegex = /^[\+]?[1-9][[\s\d]{9,16}$/

const LanguagesMessages = defineMessages({
    "none": {
        id: 'common.language.none_label',
        defaultMessage: 'None',
    },
    "en": {
        id: 'common.language.english_label',
        defaultMessage: 'English',
    },
    "fr": {
        id: 'common.language.french_label',
        defaultMessage: 'French',
    },
    "nl": {
        id: 'common.language.dutch_label',
        defaultMessage: 'Dutch',
    },
    "en-US": {
        id: 'user.form.edit.language_english_us',
        defaultMessage: 'English US',
    },
    "en-CA": {
        id: 'user.form.edit.language_english_ca',
        defaultMessage: 'English Canada',
    },
    "fr-CA": {
        id: 'user.form.edit.language_french_ca',
        defaultMessage: 'French Canada',
    },
    "nl-NL": {
        id: 'user.form.edit.language_dutch_nl',
        defaultMessage: 'Dutch Netherlands',
    },
    "es": {
        id: 'common.language.Spanish_label',
        defaultMessage: 'Spanish',
    },
})

const baseUrl = {}
const filterVersion = 5

class GlobalHelper {

    static STRIPE_CARD_BRANDS = {
        AMERICAN_EXPRESS: {
            label: 'American Express',
            value: 'amex',
        },
        DINERS_CLUB: {
            label: 'Diners Club',
            value: 'diners'
        },
        DISCOVER: {
            label: 'Discover',
            value: 'discover'
        },
        JCB: {
            label: 'Japan Credit Bureau',
            value: 'jcb'
        },
        MASTERCARD: {
            label: 'Mastercard',
            value: 'mastercard',
        },
        UNIONPAY: {
            label: 'China UnionPay',
            value: 'unionpay'
        },
        VISA: {
            label: 'Visa',
            value: 'visa',
        },
    }

    static IsFeatureFlagEnabled(featureFlag) {
        if (!GlobalHelper.isEnvProduction()) {
            return true
        }
        return !!AppContext.getAccount().attributes.flags[featureFlag]
    }

    static getObjectExternalLink(obj, integrationKey) {
        for (const link of obj.attributes.externalLinks) {
            if (link.attributes.integrationKey == integrationKey) {
                return link
            }
        }
    }

    static isValidPhoneNumberInput(phoneNumber) {
        return true//(phoneNumber && this.isValidPhoneNumber(phoneNumber) || !phoneNumber)
    }

    static checkPhoneNumberValidity(checkValidForm, phoneNumber) {
        return checkValidForm && false//(phoneNumber && !this.isValidPhoneNumber(phoneNumber))
    }

    static sortStringField(firstField, secondField, asc) {
        const ascOrder = asc ? 1 : -1
        const ascOrderSecondValue = ascOrder == 1 ? -1 : 1

        return firstField.toUpperCase() > secondField.toUpperCase() ? ascOrder
            : secondField.toUpperCase() > firstField.toUpperCase() ? ascOrderSecondValue : 0
    }

    static isEnvProduction() {
        return AppContext.getOptions().environment === "prod"
    }

    static shouldEnforceEnvFlag() {
        return ["prod", "release-candidate"].includes(AppContext.getOptions().environment)
    }

    static isSuperUserLoggedIn() {
        return AppContext.getUserLoggedIn().attributes.superUser
    }

    static redirectToPrintPdf(print_url) {
        window.open(print_url, "_blank")
    }

    static GetFormattedName = (user) => {
        return (user?.attributes?.firstName || "") + " "
            + (user?.attributes?.lastName || "")
    }

    static GetFormattedNameInitials = (user) => {
        return (user?.attributes?.firstName.charAt(0) || "")
            + (user?.attributes?.lastName.charAt(0) || "")
    }

    static getIntercomAppId() {
        let appId = "bae9d5t3"
        if (AppContext.getOptions().environment === "prod") {
            appId = "ki4yyosw"
        }
        return appId
    }

    static getAccountFlag(flag) {
        let accountInfo = AppContext.getAccount()
        if (accountInfo && accountInfo.attributes.flags) {
            return accountInfo.attributes.flags[flag]
        }
        return null
    }

    static getEmailValidationRegexString() {
        return emailValidationRegex.source
    }

    static getPostalCodeValidationString(countryCode) {
        return countryCodeRegularExpressions[countryCode]
    }

    static getPhoneNumberValidationRegexString() {
        return phoneNumberValidationRegex.source
    }

    static isValidPhoneNumber(number) {
        let formattedPhoneNumber = GlobalHelper.formatPhoneNumberDigitOnly(number)
        return formattedPhoneNumber.length >= 10;
    }

    static decimalSeparator(locale) {
        var n = 1.1;
        n = n.toLocaleString(locale).substring(1, 2);
        return n;
    }

    static thousandSeparator(locale) {
        var n = 1200;
        n = n.toLocaleString(locale).substring(1, 2);

        if (n == '2') {
            return ''
        }

        return n;
    }

    static GetLabelAndSign(rateType) {
        let feeLabel = ""
        let inputGroupText = ""
        switch (rateType) {
            case 'flat-rate':
                feeLabel = <FormattedMessage
                    id="fee.form.edit.amount_rate_label"
                    defaultMessage="Amount"
                />
                inputGroupText = GlobalHelper.getCurrencySign(AppContext.getAccount().attributes.currency)
                break
            default:
                feeLabel = <FormattedMessage
                    id="fee.form.edit.rate_label"
                    defaultMessage="Rate"
                />
                inputGroupText = "%"
                break
        }
        return { feeLabel, inputGroupText }
    }

    static getCustomerNumber(customerPhoneNumbers) {
        let customerNumber = ""
        if (customerPhoneNumbers.length > 0) {
            customerNumber = customerPhoneNumbers[0].phoneNumber
        }
        return customerNumber
    }

    static formatTwilioSmsContent(smsTemplate, publicLink, documentIdentifier) {
        const originalContent = smsTemplate.content ? smsTemplate.content : ""
        const strippedContent = originalContent.replace(/<(?:br|\/div|\/p|\/h3)>/g, "\n").replace(/<.*?>/g, "");
        const finalContent = strippedContent.replace(/{identifier}/, documentIdentifier) + "\n" + publicLink
        return finalContent
    }

    static getInternationalPhoneNumber(phoneNumber, country) {
        const countryCode = (country) ? country : "US"
        if (!isValidPhoneNumber(phoneNumber) && isSupportedCountry(countryCode)) {
            return "+" + getCountryCallingCode(countryCode) + phoneNumber
        }

        return phoneNumber
    }

    static isTwilioNumberValid(phoneNumber) {
        if (phoneNumber) {
            return isValidPhoneNumber(phoneNumber.toString())
        }
        return false
    }

    static getConversationThreadIndex(conversationThreads, threadType) {
        const matchingThreadType = (thread) => thread.type === threadType
        if (conversationThreads) {
            let index = conversationThreads.findIndex(matchingThreadType)
            return index
        }
        return -1
    }

    static getSupportedCurrencies() {
        return CurrencyCodes
    }

    static getEnabledCurrencies() {
        const currencies = AppContext.getAccount().attributes.additionalCurrencies
        currencies.unshift(AppContext.getAccount().attributes.currency)
        return this.unique(currencies)
    }

    static getSupportedLanguages() {
        return ['en', 'fr', 'nl', 'es']
    }

    static getSupportedLocales() {
        return ['en-US', 'fr', 'fr-CA', 'en-CA', 'nl-NL', 'es']
    }

    static getDefaultLocale() {
        return 'en-US'
    }

    static formatSupportedLanguage(locale) {
        const supportedLanguages = this.getSupportedLocales()
        if (supportedLanguages.includes(locale)) {
            return locale
        }
        for (let i = 0; i < supportedLanguages.length; i++) {
            if (supportedLanguages[i].split('-')[0] == locale.split('-')[0]) {
                return supportedLanguages[i]
            }
        }
        return this.getDefaultLocale()
    }

    static getLanguageMessage(language) {
        return LanguagesMessages[language];
    }

    static unique(array) {
        return [...new Set(array)]
    }

    static getCurrencySign(currency) {
        return (0).toLocaleString(
            AppContext.getLocale().getLanguage(),
            {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }
        ).replace(/\d/g, '').trim()
    }

    static getTimeZoneDifference() {
        return moment().tz(moment.tz.guess()).format('Z')
    }

    static getTimeZonePlusGMTFormat() {
        let moment = require('moment-timezone');
        let timeZones = moment.tz.names();
        timeZones = timeZones.map(tz => {
            return { value: tz, label: " (GMT" + moment.tz(tz).format('Z') + ") " + tz }
        })
        return timeZones
    }

    static integrationSupportsExternalSubscription(integrationKey) {
        return ExternalSubscriptionKeys.includes(integrationKey)
    }

    static integrationSupportsStartUpGuides(integrationKey) {
        return StartUpGuideIntegrationKeys.includes(integrationKey)
    }

    static getTimeZoneDifferenceInSeconds() {
        return parseInt(moment().tz(moment.tz.guess()).format('ZZ')) / 100 * 60 * 60
    }

    static convertTimeZoneInSecondsToHours(seconds) {
        const hours = parseInt(seconds / (60 * 60))
        let minutes = 0
        if (seconds > (hours * 60 * 60)) {
            minutes = parseInt((seconds - (hours * 60 * 60)) / 60)
        }
        let string = '+'
        if (seconds < 0) {
            string = '-'
        }
        if (hours < 10) {
            string += '0'
        }
        string += String(hours) + ':'
        if (minutes < 10) {
            string += '0'
        }
        string += String(minutes)
        return string
    }

    static formatSearchKeywords(searchKeywords) {
        if (this.isBarcode(searchKeywords)) {
            searchKeywords = this.normalizeSystemId(searchKeywords)
        }
        let keywords = searchKeywords
        if (searchKeywords.length > 1 && searchKeywords.charAt(searchKeywords.length - 1) !== " ") {
            keywords += '*'
        }
        if (!keywords) {
            keywords = '*'
        }
        return keywords
    }

    static displaySearchKeywords(searchKeywords) {
        return searchKeywords.replace(/\*/g, '').trim()
    }

    static normalizeSystemId(barcode) {
        if (barcode.substring(0, 2) === '21' && barcode.length === 13) {
            return barcode.substring(0, barcode.length - 1)
        }
        return barcode
    }

    static isBarcode(value) {
        return /^\d+$/.test(value) && value.length > 3
    }

    static getCurrencyIconValue(currency) {
        switch (currency) {
            case 'EUR':
                return 'euro-sign'
            case 'GBP':
                return 'pound-sign'
            default:
                return 'dollar-sign'
        }
    }

    static checkPasswordRules(password) {
        if (password.length < 8) {
            return false
        }
        const check = new RegExp("^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.*[!@#$%^&*])(?=.{8,})")
        return (password.length >= 8 && check.test(password))
    }

    static getSupportedCurrenciesSelectOptions() {
        return this.getSupportedCurrencies().map(c => {
            return <option value={c} key={c + "-" + uuidv4()}>{c}</option>
        })
    }

    static getEnabledCurrenciesSelectOptions() {
        return this.getEnabledCurrencies().map(c => {
            return <option value={c} key={c}>{c}</option>
        })
    }

    static applyLocaleDecimal(decimalString, locale) {
        if (typeof decimalString !== 'string') {
            decimalString = decimalString.toString()
        }
        return decimalString.replace(/\./g, GlobalHelper.decimalSeparator(locale));
    }

    static moneyFormat(num, decimals = 2) {
        if (isNaN(num)) {
            return GlobalHelper.applyLocaleDecimal("0.00", AppContext.getLocale().getLanguage())
        }
        return (Math.round(num * 100) / 100).toFixed(decimals)
    }

    static getRandomInt(max) {
        return Math.floor(Math.random() * Math.floor(max));
    }

    /*
    Returns a string that describes the city e.g. Montréal (QC), Canada
     */
    static formatCityFromAddress(address, localeWithoutRegionCode) {
        let detailedCityString = ''
        if (address && address.city) {
            detailedCityString = address.city
            const state = GlobalHelper.getStateCodeFromAddress(address, localeWithoutRegionCode)
            if (state) {
                detailedCityString += ', ' + state.toUpperCase()
            }
        }

        return detailedCityString
    }

    static formatPhoneNumber(phoneNumber, countryCode) {
        if (!phoneNumber)
            return ""

        let formattedPhoneNumber = phoneNumber
        if (phoneNumber.startsWith("+")) {
            formattedPhoneNumber = formatNumber(phoneNumber, 'International')
        } else if (countryCode && countryCode.length === 2) {
            let parsedNumber = ''
            try {
                parsedNumber = parseNumber(phoneNumber, countryCode, { extended: true })
            } catch (e) {
            }

            if (parsedNumber.possible) {
                let formatAttempt
                try {
                    formatAttempt = formatNumber(parsedNumber, 'National')
                } catch (e) {
                }
                if (formatAttempt) {
                    formattedPhoneNumber = formatAttempt
                }
            }
        }
        return formattedPhoneNumber;
    }

    static formatPhoneNumberDigitOnly(phone) {
        phone = phone.replace(/[^A-Z\d\s]/gi, '')
        phone = phone.replaceAll(" ", "")
        return phone
    }

    static getCountries(locale) {
        const countries = JSON.parse(JSON.stringify(Countries.getNames(locale)))
        const override = CountryOverride[locale] ? JSON.parse(JSON.stringify(CountryOverride[locale])) || {} : {}
        for (let key in override) {
            countries[key] = override[key]
        }
        return countries
    }

    static getCountryLocalizedName(countryCode, localeWithoutRegionCode) {
        if (!countryCode) {
            return ''
        }
        const countries = this.getCountries(localeWithoutRegionCode)
        return countries[countryCode] || ''
    }

    static getCountryCodeFromLocalizedName(countryName, localeWithoutRegionCode) {
        if (!countryName) {
            return ''
        }
        const countries = this.getCountries(localeWithoutRegionCode)
        for (let code in countries) {
            if (countries[code].toLowerCase() === countryName.toLowerCase()) {
                return code
            }
        }
        return ''
    }

    static getCountryCodeFromAddress(address, localeWithoutRegionCode, defaultCountryCode) {
        if (address.countryCode) {
            return address.countryCode
        } else if (address.country) {
            return GlobalHelper.getCountryCodeFromLocalizedName(address.country, localeWithoutRegionCode)
        }
        return defaultCountryCode || ''
    }

    static getLocalizedTitle(title, localeWithoutRegionCode) {
        let localizedTitle = ''
        if (title === 'Mr') {
            switch (localeWithoutRegionCode.toLowerCase()) {
                case "en":
                    localizedTitle = 'Mr';
                    break;
                case "fr":
                    localizedTitle = 'M.';
                    break;
            }

        }
        if (title === 'Ms') {
            switch (localeWithoutRegionCode.toLowerCase()) {
                case "en":
                    localizedTitle = 'Ms';
                    break;
                case "fr":
                    localizedTitle = 'Mme';
                    break;
            }
        }

        return localizedTitle
    }

    /**
     * Detect the current active responsive breakpoint in Bootstrap
     * @returns {string}
     * @author farside {@link https://stackoverflow.com/users/4354249/farside}
     */
    static getResponsiveBreakpoint() {
        var envs = { xs: "d-none", sm: "d-sm-none", md: "d-md-none", lg: "d-lg-none", xl: "d-xl-none" };
        var env = "";

        var $el = $("<div>");
        $el.appendTo($("body"));

        for (var i = Object.keys(envs).length - 1; i >= 0; i--) {
            env = Object.keys(envs)[i];
            $el.addClass(envs[env]);
            if ($el.is(":hidden")) {
                break; // env detected
            }
        }
        $el.remove();
        return env;
    };

    static getStateLocalizedName(countryCode, stateCode, localeWithoutRegionCode) {
        if (!stateCode) {
            return ''
        }

        var localizedStateNames = provinces.filter(p => p.country === countryCode && p.short === stateCode && p.locale === localeWithoutRegionCode)
        if (localizedStateNames.length > 0) {
            return localizedStateNames[0]["name"]
        }

        return ''
    }

    static getStateCodeFromStateName(countryCode, stateName, localeWithoutRegionCode) {
        if (!stateName) {
            return ''
        }
        const localizedStateNames = provinces.filter(p => p.country === countryCode && p.name === stateName && p.locale === localeWithoutRegionCode)[0]
        if (localizedStateNames) {
            return localizedStateNames.short
        }
        return ''
    }

    static getStateCodeFromAddress(address, localeWithoutRegionCode) {
        if (address.stateCode) {
            return address.stateCode
        } else if (address.state) {
            const countryCode = this.getCountryCodeFromAddress(address, localeWithoutRegionCode)
            return GlobalHelper.getStateCodeFromStateName(countryCode, address.state, localeWithoutRegionCode)
        }
        return ''
    }

    static saveListingPreferences(type, page, pageSize, keywords, statBox, includeCreditMemos, advancedSearch, sortField, asc, filtersToUse, savedFilterId, view, nestedSortFilters) {
        const url = '/users/' + AppContext.getUserLoggedIn().id + '/preferences'
        return AppContext.getApiClient().patch(url, {
            [type]: Base64.encode(JSON.stringify({
                page: page,
                pageSize: pageSize,
                keywords: keywords,
                statBox: statBox,
                includeCreditMemos: includeCreditMemos,
                advancedSearch: advancedSearch || false,
                sortField: sortField || '',
                asc: asc || false,
                filters: filtersToUse,
                version: filterVersion,
                savedFilterId: savedFilterId || '',
                view,
                nestedSortFilters,
            }))
        }).then(res => {
            GlobalCache[url] = res
            return Promise.resolve(res)
        })
    }

    static decodeSavedFilter(filter) {
        var savedFilter = JSON.parse(Base64.decode(filter));
        return savedFilter["filters"];
    }

    static saveFilterPreferences(stype, page, pageSize, keywords, statBox, includeCreditMemos, sortField, asc, filtersToUse, filterOptions) {
        const url = '/users/' + AppContext.getUserLoggedIn().id + '/preferences'
        return AppContext.getApiClient().patch(url, {
            filterContent: Base64.encode(JSON.stringify({
                page: page,
                pageSize: pageSize,
                keywords: keywords,
                statBox: statBox,
                includeCreditMemos: includeCreditMemos,
                sortField: sortField || '',
                asc: asc || false,
                filters: filtersToUse,
                filterOptions: filterOptions,
            }))
        }).then(res => {
            GlobalCache[url] = res
            return Promise.resolve(res)
        })
    }

    static getListingPreferences(type, checkVersion) {
        const url = '/users/' + AppContext.getUserLoggedIn().id + '/preferences'
        let promise = {}
        if (GlobalCache[url]) {
            promise = Promise.resolve(GlobalCache[url])
        } else {
            promise = AppContext.getApiClient().get(url)
        }
        return promise.then(res => {
            GlobalCache[url] = res
            const pref = this.decodePreferences(url, type)

            if (checkVersion && (pref.version === undefined || filterVersion > pref.version)) {
                return Promise.resolve({
                    page: 1,
                    pageSize: 10,
                    keywords: '',
                    filters: {},
                    includeCreditMemos: false,
                    advancedSearch: false,
                    statBox: "",
                    sortField: '',
                    asc: false,
                    view: {
                        columns: []
                    }
                })
            } else {
                return Promise.resolve({
                    page: pref.page || 1,
                    pageSize: pref.pageSize || 10,
                    keywords: pref.keywords || '',
                    filters: pref.filters || {},
                    includeCreditMemos: pref.includeCreditMemos || false,
                    advancedSearch: pref.advancedSearch || false,
                    statBox: pref.statBox || "",
                    sortField: pref.sortField || '',
                    asc: pref.asc || false,
                    savedFilterId: pref.savedFilterId || '',
                    view: pref.view || { columns: [] },
                    nestedSortFilters: pref.nestedSortFilters || {}
                })
            }
        })
    }

    static decodePreferences(key, type) {
        const res = GlobalCache[key]
        if (!res) {
            return {}
        }
        let pref = {}
        try {
            pref = JSON.parse(Base64.decode(res.attributes[type]))
        } catch (e) {
            pref = {}
        }
        return pref
    }

    static hasAtLeastOneListingFilter(type) {
        const pref = this.decodePreferences('/users/' + AppContext.getUserLoggedIn().id + '/preferences', type)
        let hasFilters = false
        if (pref.filters) {
            for (let key in pref.filters) {
                let value = pref.filters[key]
                if (Array.isArray(value) && value.length > 0) {
                    hasFilters = true
                }
                if ((typeof value === 'string' || value instanceof String) && value !== '') {
                    hasFilters = true
                }
            }
        }
        return pref.page > 1 || pref.keywords !== '' || hasFilters
    }

    static captureFingerprint(url, accountId) {
        fetch('https://us-central1-tactiq-production.cloudfunctions.net/geolocation-prod-3')
            .then(function (response) {
                return response.json()
            })
            .then(res => {
                const latLong = res.cityLatLong?.split(',') ?? []
                return AppContext.getApiClient().post('/fingerprints', {
                    ip: res.ip,
                    locale: AppContext.getLocale().getLanguage(),
                    url: url,
                    accountId: accountId,
                    userAgent: navigator.userAgent,
                    city: res.city === '?' ? '' : res.city,
                    countryCode: res.country === '?' ? '' : res.country,
                    regionCode: res.region === '?' ? '' : res.region,
                    cityLatitude: parseFloat(latLong[0] || '0'),
                    cityLongitude: parseFloat(latLong[1] || '0'),
                })
            }).then(res => {
                window.qmFingerprint = res
            })
    }

    static getFingerprintId() {
        if (window.qmFingerprint) {
            return window.qmFingerprint.id
        }
        return ''
    }

    static formatContactEmail(email, name) {
        // Formatting the name to prevent an edge case with a single email and a name containing semi colons or commas
        return email.includes(',') ||
            email.includes(';') ?
            email :
            name.replace(/[;,]/g, '') + " <" + email.trim() + ">"
    }

    static validateEmail(email) {
        const re = emailValidationRegex
        return re.test(String(email).toLowerCase())
    }

    static getCountryCodeFromCurrency(currency) {
        if (currency === 'USD') {
            return 'US'
        }
        return 'CA'
    }

    static GetEnabledIntegrationConfigByKey(integrationKey) {
        return AppContext.integrations
            .filter(i => i.attributes.enabled &&
                i.attributes.integrationKey === integrationKey) ?? []
    }

    static getSupportedDocuments() {
        let documents = ['quotes', 'orders', 'recurring-invoices', 'invoices', 'price-lists', 'scheduling-pages', 'contracts', 'credit-memos', 'account-statements', 'packing-slips', 'events', 'snippet']
        let activeIntegrations = AppContext.integrations.filter(i => i.attributes.enabled).map(i => i.attributes.integrationKey)
        for (let i in activeIntegrations) {
            const details = Integrations.getIntegrationDetails(activeIntegrations[i])
            const incompatibleDocuments = details.incompatibleDocuments || []
            documents = documents.filter(d => !incompatibleDocuments.includes(d))
        }
        return documents
    }

    static isDocumentSupported(document) {
        const supportedDocuments = GlobalHelper.getSupportedDocuments()
        return supportedDocuments.includes(document)
    }

    static isBrowserExtensionInstalled() {
        return document.querySelector('#qm-browser-extension-version').getAttribute('installed') === 'true'
    }

    static hasSupportedFeature(featureName) {
        let integrations = AppContext.integrations;
        let isFeatureSupported = false
        for (let integration of integrations) {
            if (!integration.attributes.enabled) {
                continue
            }
            if (integration.attributes.features.find(feature => feature === featureName)) {
                isFeatureSupported = true
                break
            }
        }
        let isFeatureEnabled = AppContext.getIntegrationByFeature(Features.featureName)
        if ((isFeatureEnabled && isFeatureSupported) || (isFeatureEnabled === undefined && isFeatureSupported)) {
            return true
        }
        return false
    }

    static hasIntegration(integrationKey) {
        return AppContext.integrations.filter(i => [integrationKey].includes(i.attributes.integrationKey) && i.attributes.enabled && !i.attributes.configuration.testMode).length > 0
    }

    static hasLightspeedIntegration() {
        return AppContext.integrations.filter(i => i.attributes.integrationKey === INTEGRATION_KEY.LIGHTSPEED_RETAIL && i.attributes.enabled).length > 0
    }

    static getAllActiveIntegrations() {
        return AppContext.integrations.filter(i => i.attributes.enabled) || []
    }

    static hasShopifyExpertAppIntegration() {
        return AppContext.integrations.filter(i => i.attributes.integrationKey === 'shopify-expert-app' && i.attributes.enabled).length > 0
    }

    static hasLightspeedEcomIntegration() {
        return AppContext.integrations.filter(i => i.attributes.integrationKey === 'ls-ecom' && i.attributes.enabled).length > 0
    }

    static hasLightspeedEcomOmniIntegration() {
        return AppContext.integrations.filter(i => i.attributes.integrationKey === 'ls-ecom-omni' && i.attributes.enabled).length > 0
    }

    static hasLightspeedEcomOmniNaIntegration() {
        return AppContext.integrations.filter(i => i.attributes.integrationKey === 'ls-ecom-omni-na' && i.attributes.enabled).length > 0
    }

    static hasOneOfTheLightspeedEcomIntegrations() {
        return this.hasLightspeedEcomIntegration() || this.hasLightspeedEcomOmniIntegration() || this.hasLightspeedEcomOmniNaIntegration()
    }

    static hasVendIntegration() {
        return AppContext.integrations.filter(i => i.attributes.integrationKey === INTEGRATION_KEY.VEND && i.attributes.enabled).length > 0
    }

    static hasQuickBooksIntegration() {
        return AppContext.integrations.filter(i => i.attributes.integrationKey === 'quick-books' && i.attributes.enabled).length > 0
    }

    static hasStripeIntegration() {
        return AppContext.integrations.filter(i => ['stripe'].includes(i.attributes.integrationKey) && i.attributes.enabled && !i.attributes.configuration.testMode).length > 0
    }

    static getIntegration(integrationKey) {
        for (let integration of AppContext.integrations) {
            if (integration.attributes.integrationKey === integrationKey) {
                return integration
            }
        }
        return null
    }

    static getIntegrationsWithEnabledFeature(featureKey) {
        let integrations = AppContext.integrations.filter(i => i.attributes.enabled)
        let integrationsWithEnabledFeature = []
        for (let integration of integrations) {
            //to be considered enabled, integration must support the feature and have it as enabled or implicitly enabled as undefined
            const isSupported = integration.attributes.features.includes(featureKey)
            const isEnabled = integration.attributes.enabledFeatures[featureKey]
            const isUndefined = isEnabled === undefined

            if (isSupported && (isEnabled || isUndefined)) {
                integrationsWithEnabledFeature.push(integration)
            }
        }
        return integrationsWithEnabledFeature
    }

    static getFirstIntegrationFoundWithEnabledFeature(featureKey) {
        const integrations = this.getIntegrationsWithEnabledFeature(featureKey)
        if (integrations.length > 0) {
            return integrations[0]
        }

        return null
    }

    static getFirstIntegrationKeyFoundWithEnabledFeature(featureKey) {
        const integration = this.getFirstIntegrationFoundWithEnabledFeature(featureKey)
        if (!integration) {
            return ''
        }

        return integration.attributes.integrationKey
    }

    static getIntegrationKeysOfSupportedFeature(featureName) {
        return this.getIntegrationsWithSupportedFeature(featureName).map(i => i.attributes.integrationKey)
    }

    static getIntegrationsWithSupportedFeature(featureName) {
        let integrations = []
        AppContext.integrations.forEach(integration => {
            if (integration.attributes.enabled && integration.attributes.features.includes(featureName)) {
                integrations.push(integration)
            }
        })
        return integrations
    }


    static canQMInventoryBeEnabled() {
        if (!GlobalHelper.isEnvProduction()) {
            return true
        }

        for (let integration of this.getIntegrationsWithEnabledFeature(FeatureInventorySync) ?? []) {
            switch (integration.attributes.integrationKey) {
                case INTEGRATION_KEY.QUICK_BOOKS:
                    const configToggles = integration.attributes.configuration?.configToggles
                    if (configToggles?.["isProductSyncEnabled"] || configToggles?.["isInventorySyncEnabled"]) {
                        return false
                    }
                    break
                default:
                    return false
            }
        }

        if (this.getIntegrationsWithEnabledFeature(FeaturePurchaseOrderTracking).length > 0) {
            return false
        }

        return AppContext.accountFeatureIsEnabled(AccountFeatures.FeatureInventory)

    }

    static isQBOInventorySyncVisible(inventoryEnabled) {
        if (this.getIntegrationKeysOfSupportedFeature(FeatureInventorySync).length > 1) {
            return true
        }
        let qboIntegration = GlobalHelper.getIntegration("quick-books")
        if (qboIntegration) {
            if (qboIntegration.attributes.configuration.configToggles) {
                return (qboIntegration.attributes.configuration.configToggles["isProductSyncEnabled"] && qboIntegration.attributes.enabled)
            } else {
                return false
            }
        }
        return inventoryEnabled
    }

    static hasActiveInventorySync() {
        if (this.hasExternalInventoryIntegration()) {
            let qboIntegration = GlobalHelper.getIntegration("quick-books")
            if (qboIntegration && qboIntegration.attributes.enabled && qboIntegration.attributes.configuration) {
                if (qboIntegration.attributes.configuration.configToggles?.["isInventorySyncEnabled"]
                    || qboIntegration.attributes.enabledFeatures["purchase-order-tracking"]
                    || qboIntegration.attributes.configuration.configToggles?.["isProductSyncEnabled"]) {
                    return true
                } else {
                    return false
                }
            } else {
                return true
            }
        }
        return false
    }

    static hasAnotherIntegrationWithFeatureEnabled(integrationKey, featureKey) {
        for (let integration of this.getIntegrationKeysOfSupportedFeature(featureKey)) {
            if (integration === integrationKey) {
                continue
            }
            let supportedIntegration = this.getIntegration(integration)
            if (supportedIntegration.attributes.enabledFeatures[featureKey]) {
                return true
            }
        }
        return false
    }

    static getAllStripeIntegrationsPaymentMethodOptions() {
        const integrations = AppContext.integrations.filter(i => {
            return ['stripe'].includes(i.attributes.integrationKey) &&
                i.attributes.enabled
        })

        let paymentMethodOptions = []
        let paymentMethodOptionsSet = new Set()

        for (let integration of integrations) {
            for (let featureKey of Object.keys(integration.attributes.enabledFeatures)) {
                if (!integration.attributes.enabledFeatures[featureKey]) {
                    continue
                }

                if (ExternalPaymentTypesMapping[featureKey] && !paymentMethodOptionsSet.has(featureKey)) {
                    paymentMethodOptionsSet.add(featureKey)
                    paymentMethodOptions.push({
                        value: featureKey,
                        label: ExternalPaymentTypesMapping[featureKey],
                    })
                }
            }
        }

        return paymentMethodOptions
    }

    static hasPaysafeIntegration() {
        return AppContext.integrations.filter(i => ['paysafe'].includes(i.attributes.integrationKey) && i.attributes.enabled && !i.attributes.configuration.testMode).length > 0
    }

    static hasShopifyIntegration() {
        return AppContext.integrations.filter(i => ShopifyAppIntegrationKeys.includes(i.attributes.integrationKey) && i.attributes.enabled && !i.attributes.configuration.testMode).length > 0
    }

    static hasGolfIntegration() {
        return AppContext.integrations.filter(i => ['ls-golf'].includes(i.attributes.integrationKey) && i.attributes.enabled && !i.attributes.configuration.testMode).length > 0
    }

    static getShopifyIntegration() {
        return AppContext.integrations.find(i => ShopifyAppIntegrationKeys.includes(i.attributes.integrationKey) && i.attributes.enabled && !i.attributes.configuration.testMode)
    }

    static hasIntegrations(integrationKeys) {
        return AppContext.integrations.find(i => integrationKeys.includes(i.attributes.integrationKey) && i.attributes.enabled && !i.attributes.configuration.testMode)
    }

    static getShopifyIntegrationOrigin() {
        const shopifyIntegration = this.getShopifyIntegration()
        return shopifyIntegration ? shopifyIntegration.attributes.externalAccountId : ''
    }

    static getShopifyIntegrationHost() {
        const shopifyIntegration = this.getShopifyIntegration()
        return shopifyIntegration ? shopifyIntegration.attributes.configuration.host : ''
    }

    static hasShopifyIntegrationWithDisabledFeatures() {
        const shopifyIntegration = this.getShopifyIntegration()
        if (!shopifyIntegration) {
            return false
        }
        const enabledFeatures = shopifyIntegration.attributes.enabledFeatures
        return !enabledFeatures['enable-hidden-features']
    }

    static hasShopifyIntegrationWithEnabledInboxFeature() {
        const shopifyIntegration = this.getShopifyIntegration()
        if (!shopifyIntegration) {
            return false
        }
        const enabledFeatures = shopifyIntegration.attributes.enabledFeatures
        return enabledFeatures['enable-inbox-feature']
    }

    static hasExternalInventoryIntegration() {
        return this.getIntegrationKeysOfSupportedFeature(FeatureInventorySync).length > 0
    }

    static hasActivePaymentIntegration() {
        const env = AppContext.getOptions().environment
        if (env !== "prod") {
            return true
        }
        return AppContext.integrations.filter(i => ['stripe', 'paysafe'].includes(i.attributes.integrationKey) && i.attributes.enabled && !i.attributes.configuration.testMode).length > 0
    }

    static canEditLocation() {
        if (AppContext.getUserLoggedIn().attributes.superUser) {
            return true
        }
        return AppContext.isMultiLocation() && AppContext.getUserLoggedIn().attributes.role.attributes.permissions["access-all-locations"] == true
    }

    static hasAccessToAllLocations() {
        if (AppContext.getUserLoggedIn().attributes.superUser) {
            return true
        }
        return AppContext.getUserLoggedIn().attributes.role.attributes.permissions["access-all-locations"] == true
    }

    static isAdminListing() {
        if (AppContext.getUserLoggedIn().attributes.superUser || AppContext.getUserLoggedIn().attributes.role.attributes.permissions["access-all-locations"] == true) {
            return true
        }
        return false
    }

    static canEditCreditMemoContact(creditMemo) {
        return AppContext.getUserLoggedIn().attributes.role.attributes.key === "admin" &&
            creditMemo && !creditMemo.attributes.payment?.attributes.externalLink &&
            creditMemo.attributes.externalLinks.length == 0
    }

    static canReplaceCustomerInCreditMemo(creditMemo) {
        return AppContext.getUserLoggedIn().attributes.superUser || this.canEditCreditMemoContact(creditMemo)
    }

    static getLocationById(locationId) {
        return AppContext.locations.filter(loc => loc.id === locationId)[0]
    }

    static findLocationById(locationId) {
        return AppContext.locations.find(loc => loc.id === locationId)
    }

    static getIntegrationById(integrationId) {
        return AppContext.integrations.filter(integration => integration.id === integrationId)[0]
    }

    static isLocationAssigned(locationId) {
        return AppContext.integrations.some(i => i.attributes.locationIds?.includes(locationId));
    }

    static isLocationAssignedToIntegrationKey(locationId, integrationKey) {
        return AppContext.integrations.some(i => i.attributes.locationIds?.includes(locationId) && i.attributes.integrationKey === integrationKey && i.attributes.enabled) ||
            !this.isLocationAssigned(locationId) && AppContext.integrations.some(i => i.attributes.locationIds?.length == 0 && i.attributes.integrationKey === integrationKey && i.attributes.enabled)
    }

    static isLocationAssignedToIntegrationId(locationId, id) {
        const hasEnabledIntegrationWithSpecifiedLocationId = AppContext.integrations.some(i => i.id === id && i.attributes.enabled && i.attributes.locationIds?.includes(locationId))
        const isHandledByEnabledIntegrationWithAllLocation = !this.isLocationAssigned(locationId) && AppContext.integrations.some(i => i.id === id && i.attributes.enabled && i.attributes.locationIds?.length == 0)
        return hasEnabledIntegrationWithSpecifiedLocationId || isHandledByEnabledIntegrationWithAllLocation
    }


    static getQuantityOnHandFromFullProduct(product, currentLoc) {
        let loc = AppContext.getUserLoggedIn().attributes.location
        if (currentLoc) {
            loc = currentLoc
        }
        if (!loc) {
            return product.attributes.quantityOnHand
        }
        const locationQuantityOnHand = product.attributes.locationQuantitiesOnHand.filter(q => q.attributes.locationId === loc.id)[0]
        if (!locationQuantityOnHand) {
            return product.attributes.quantityOnHand
        }
        return locationQuantityOnHand.attributes.quantityOnHand
    }

    static getQuantityOnHand(product, currentLoc) {
        let loc = AppContext.getUserLoggedIn().attributes.location
        if (currentLoc) {
            loc = currentLoc
        }
        if (!loc) {
            return product.quantityOnHand
        }
        const locationQuantityOnHand = product.locationQuantitiesOnHand ? product.locationQuantitiesOnHand.find(q => q.locationId === loc.id) : null
        if (!locationQuantityOnHand) {
            return product.quantityOnHand
        }
        return locationQuantityOnHand.quantityOnHand
    }

    static getRemoteQuantityOnHand(product, locationToExclude) {
        let loc = null
        if (locationToExclude) {
            loc = locationToExclude
        }
        if (!loc) {
            loc = AppContext.getUserLoggedIn().attributes.location
        }

        let remoteQuantity = 0
        if (product.locationQuantitiesOnHand) {
            let remoteQuantity = product.locationQuantitiesOnHand.reduce((accumulator, locationId) => {
                let quantityOnHand = locationId.quantityOnHand
                if (loc && loc.id && locationId.locationId === loc.id) {
                    quantityOnHand = 0
                }
                return accumulator + parseFloat(quantityOnHand)
            }, 0)
            return remoteQuantity
        }
        return remoteQuantity
    }

    static getRemoteQuantitesOnHandByLocation(product, currentLoc) {
        if (!currentLoc) {
            currentLoc = AppContext.getUserLoggedIn().attributes.location
        }
        let quantitiesOnHandByLocation = []

        for (let location of AppContext.locations) {
            const isCurrentLocation = currentLoc && currentLoc.id === location.id
            const locationFound = product.attributes.locationQuantitiesOnHand.find(loc => loc.attributes.locationId === location.id)
            let quantityOnHand = {
                name: location.attributes.name,
                quantityOnHand: '0',
                isCurrentLocation,
            }
            if (locationFound) {
                quantityOnHand.quantityOnHand = locationFound.attributes.quantityOnHand
            }

            if (isCurrentLocation) {
                quantitiesOnHandByLocation.unshift(
                    quantityOnHand,
                )
            } else {
                quantitiesOnHandByLocation.push(
                    quantityOnHand,
                )
            }
        }
        return quantitiesOnHandByLocation
    }

    static isMobile() {
        const userAgent = navigator.userAgent || ''
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
            return true
        }
        return false
    }

    static isIntegratedPaymentBankTransferEnabled() {
        return AppContext.hasFeatureIntegration(Features.IntegratedPaymentBankTransfer) &&
            AppContext.accountFeatureIsEnabled(AccountFeatures.FeatureIntegratedBankTransfer)
    }

    static getPaymentMethods() {
        const paymentMethods = []
        if (AppContext.hasFeatureIntegration(Features.IntegratedPayment)) {
            paymentMethods.push('credit-card')
        }
        if (this.isIntegratedPaymentBankTransferEnabled) {
            paymentMethods.push('bank-transfer')
        }
        return paymentMethods
    }

    static getPaymentMethodsCheckout() {
        const paymentMethods = []
        if (AppContext.hasFeatureIntegration(Features.IntegratedPayment)) {
            paymentMethods.push(PAYMENT_TYPE.CREDIT_CARD)
        }

        paymentMethods.push(PAYMENT_TYPE.ON_ACCOUNT)

        const haveRFIntegrationEnabled = !!this.GetEnabledIntegrationConfigByKey(INTEGRATION_KEY.RAINFOREST).length
        const showBankTransferMethod = haveRFIntegrationEnabled && this.isIntegratedPaymentBankTransferEnabled

        if (showBankTransferMethod) {
            paymentMethods.push(PAYMENT_TYPE.BANK_TRANSFER)
        }

        return paymentMethods
    }

    static getPaymentMethodOptions() {
        const labels = {
            'credit-card': <FormattedMessage
                id="templates.sections.form.edit.content.payment_section.credit_card_option_label"
                defaultMessage="Credit card"
            />,
            'bank-transfer': <FormattedMessage
                id="templates.sections.form.edit.content.payment_section.bank_transfer_option_label"
                defaultMessage="Bank transfer"
            />,
        }
        return this.getPaymentMethods().map(m => {
            return {
                value: m,
                label: labels[m]
            }
        })
    }

    static getCardBrandOptions() {
        return Object.values(GlobalHelper.STRIPE_CARD_BRANDS)
    }

    static getCardBrandLabelByValue(value) {
        const brand = GlobalHelper.getCardBrandOptions().find(brand => brand.value === value)
        return brand ? brand.label : ''
    }

    static getPaymentMethodOptionsCheckout() {
        const labels = {
            'credit-card': <FormattedMessage
                id="templates.sections.form.edit.content.payment_section.credit_card_option_label"
                defaultMessage="Credit card"
            />,
            'on-account': <FormattedMessage
                id="templates.sections.form.edit.content.payment_section.on_account_option_label"
                defaultMessage="On Account"
            />,
        }

        const haveRFIntegrationEnabled = !!this.GetEnabledIntegrationConfigByKey(INTEGRATION_KEY.RAINFOREST).length
        const showBankTransferLabel = haveRFIntegrationEnabled && this.isIntegratedPaymentBankTransferEnabled

        if (showBankTransferLabel) {
            labels['bank-transfer'] = <FormattedMessage
                id="templates.sections.form.edit.content.payment_section.bank_transfer_option_label"
                defaultMessage="Bank transfer"
            />
        }

        return this.getPaymentMethodsCheckout().map(m => {
            return {
                value: m,
                label: labels[m]
            }
        })
    }

    static getOrderStatusColor(statusId) {
        const status = AppContext.getOrderStatusById(statusId)
        let type = ''
        if (status && status.attributes.type) {
            type = status.attributes.type
        }
        switch (type) {
            case 'ordered':
                return 'warning'
            case 'completed':
                return 'success'
            default:
                return 'secondary'
        }
    }

    static getOrderStatusName(statusId) {
        const status = AppContext.getOrderStatusById(statusId)
        let name = ''
        if (status && status.attributes.type) {
            name = status.attributes.name
        }
        return name
    }

    static compareItemsByGroupAndRank(item1, item2, groups) {
        let groupIdRank = new Map();
        for (let i = 0; i < groups.length; i++) {
            groupIdRank.set(groups[i].id, groups[i].attributes.rank)
        }

        let groupIdItem1 = item1.attributes.groupId !== "" ? groupIdRank.get(item1.attributes.groupId) : groups.length + 1
        let groupIdItem2 = item2.attributes.groupId !== "" ? groupIdRank.get(item2.attributes.groupId) : groups.length + 1

        let rankItem1 = item1.attributes.rank
        let rankItem2 = item2.attributes.rank


        if (groupIdItem1 < groupIdItem2) {
            return -1
        } else if (groupIdItem1 > groupIdItem2) {
            return 1
        } else {
            return rankItem1 < rankItem2 ? -1 : 1
        }
    }

    static getEmailTemplateByProposalType(proposalType) {
        if (proposalType === 'quote') {
            return 'send-proposal'
        }
        return 'send-' + proposalType
    }

    static getSampleQuoteUrl() {
        return 'https://qmach.in/p/proposals/43db2a92efbc82ba9bdfa46bf40993bd0676d2e0/sample'
    }

    static getSampleOrderUrl() {
        return 'https://qmach.in/p/proposals/53541fb152db23239ea24b27031b7f57fbdac27e/sample'
    }

    static getSampleInvoiceUrl() {
        return 'https://qmach.in/p/proposals/13ee8b8708c5f86af215c5a7ac008ba238e6215b/sample'
    }

    static isValidUrl(str) {
        var pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
            '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
        return !!pattern.test(str);
    }

    static getDemoLink() {
        switch (AppContext.getLocale().getLanguageWithoutRegionCode()) {
            case 'en':
                return 'https://www.quotemachine.com/en/schedule-demo/'
            case 'fr':
                return 'https://www.quotemachine.com/fr/obtenir-une-demo/'
            default:
                return 'https://www.quotemachine.com/en/schedule-demo/'
        }
    }

    static getObjectEditPathByObjectType(objectId, objectType) {
        switch (objectType) {
            case 'returns':
                return `/returns/${objectId}/edit`
            case 'quotes':
                return `/quotes/${objectId}/edit`
            case 'invoices':
                return `/invoices/${objectId}/edit`
            case 'orders':
                return `/orders/${objectId}/edit`
            default:
                return ''
        }
    }

    static isAccountLimitExceeded(accountLimit) {
        if (!accountLimit || (accountLimit && !accountLimit.attributes)) { return false }
        const usageCount = Number(accountLimit.attributes.documentUsage)
        const usageLimit = Number(accountLimit.attributes.documentLimit)
        const isEnforced = accountLimit.attributes.isLimitEnforced

        if (usageLimit == 0 || !isEnforced) { return false }
        return usageCount >= usageLimit
    }

    static hasIntegrationSupportingExternalQuoteRequest() {
        return AppContext.integrations.filter(i =>
            ['shopify', 'shopify-expert-app', 'ls-ecom'].includes(i.attributes.integrationKey) &&
            i.attributes.enabled
        ).length > 0
    }

    static getIntegrationSupportingEcommerceConfiguration() {
        let integrations = this.getIntegrationKeysOfSupportedFeature(FeatureEcommerceConfiguration)
        if (integrations) {
            return integrations[0]
        }
    }

    static activeIntegrationSupportsInventorySync() {
        const supportedIntegrations = [INTEGRATION_KEY.FRESHBOOKS, INTEGRATION_KEY.WIX];
        return this.getAllActiveIntegrations().some(intg => {
            if (intg.attributes.integrationKey && supportedIntegrations.includes(intg.attributes.integrationKey)) {
                return true
            }
            return false
        })
    }

    static shouldDisplayInvoiceManualExportButton(invoiceAttributes, integrationKey, isReturn) {
        // If invoice/payment is already exported (have an external link) > DO NOT SHOW the Export button
        const alreadyExported = invoiceAttributes.externalLinks.find(externalLink => externalLink.attributes.integrationKey === integrationKey)
        if (alreadyExported) return false

        // If the invoice is actually a refund for a credit memo, DO NOT SHOW the Export button
        const refundFromCreditMemo = invoiceAttributes.refundFromCreditMemoId !== ""
        if (refundFromCreditMemo) return false

        // if no (any) shopify integration > SHOW the Export button
        const shopifyIntegration = this.getShopifyIntegration()
        if (!shopifyIntegration) return true

        // If invoice is partially or fully paid and invoice DOES NOT have an external link (manually removed) > SHOW the Export button
        if (invoiceAttributes.status === INVOICE_STATUS.PARTIALLY_PAID || invoiceAttributes.status === INVOICE_STATUS.FULLY_PAID) return true

        // if shopify feature for full payment at checkout only ENABLED > DO NOT SHOW the Export button
        // if shopify feature for full payment at checkout only NOT ENABLED > SHOW the Export button
        const enabledFeatures = shopifyIntegration.attributes.enabledFeatures
        return !enabledFeatures[FeatureFullPaymentAndCheckoutOnly]
    }

    static InIFrame() {
        return window.self !== window.top
    }

    static isPaymentGatewayTestMode() {
        return AppContext.getAccount().attributes.paymentGatewayTestMode
    }

    static getAllEnabledPaymentIntegrations() {
        return AppContext.integrations.filter(i => PAYMENT_INTEGRATION_KEYS.includes(i.attributes.integrationKey))
            .filter(i => i.attributes.enabled)
    }

    static getPaymentIntegrationPerLocation(locationId) {
        const paymentIntegrations = this.getAllEnabledPaymentIntegrations()
        return paymentIntegrations.find(i => i.attributes.locationIds.includes(locationId))
            || paymentIntegrations.find(i => i.attributes.locationIds.length === 0 && !this.isLocationAssigned(locationId)) //all unassigned locations
    }

    static areAllEnabledLocationsAssignedToOneIntegration() {
        if (AppContext.locations.length === 0) {
            //"all" locations are assigned if there's none in the first place
            return true
        }
        const paymentIntegrations = this.getAllEnabledPaymentIntegrations()
        if (paymentIntegrations.length != 1) {
            return false
        }
        const integration = paymentIntegrations[0]
        const locations = AppContext.locations.filter(loc => loc.attributes.enabled)
        if (locations.length == 0) {
            return false
        }
        for (const location of locations) {
            if (!this.isLocationAssignedToIntegrationId(location.id, integration.id)) {
                return false
            }
        }
        return true
    }

    static hasOnlyOneAllPaymentIntegration() {
        let hasOnlyOneAllPaymentIntegration = false
        const paymentIntegrations = this.getAllEnabledPaymentIntegrations()
        if (paymentIntegrations.length === 1) {
            hasOnlyOneAllPaymentIntegration = paymentIntegrations[0].attributes.locationIds.length === 0
        }

        return hasOnlyOneAllPaymentIntegration
    }

    static hasOnlyOneAllPaymentIntegrationOrNone() {
        const paymentIntegrations = this.getAllEnabledPaymentIntegrations()
        return paymentIntegrations.length === 0 || this.hasOnlyOneAllPaymentIntegration()
    }

    static getCardOnFilePerIntegrationLocation = (locationId, contact) => {

        const paymentIntegrationPerLocation = this.getPaymentIntegrationPerLocation(locationId)

        if (!paymentIntegrationPerLocation) {
            return
        }

        const cardsOnFilePerPaymentIntegration = contact.attributes.cardsOnFile
            .filter(card => card.attributes.integrationConfigurationId === paymentIntegrationPerLocation?.id)
            .sort((a, b) => a.attributes.card.attributes.last4 < b.attributes.card.attributes.last4 ? -1 : 1) //ascending order

        const cardWithDefaultValue = cardsOnFilePerPaymentIntegration.find(c => c.attributes.isDefault)

        return cardWithDefaultValue !== undefined ? cardWithDefaultValue : cardsOnFilePerPaymentIntegration[0]

    }

    static getBankAccountOnFilePerIntegrationLocation = (locationId, contact) => {

        const paymentIntegrationPerLocation = this.getPaymentIntegrationPerLocation(locationId)

        if (!paymentIntegrationPerLocation) {
            return
        }

        const bankAccountsOnFilePerPaymentIntegration = contact.attributes.bankAccountsOnFile
            .filter(ba => ba.attributes.integrationConfigurationId === paymentIntegrationPerLocation?.id)
            .sort((a, b) => a.attributes.bankAccount.attributes.last4 < b.attributes.bankAccount.attributes.last4 ? -1 : 1) //ascending order

        const bankAccountWithDefaultValue = bankAccountsOnFilePerPaymentIntegration.find(c => c.attributes.isDefault)

        return bankAccountWithDefaultValue !== undefined ? bankAccountWithDefaultValue : bankAccountsOnFilePerPaymentIntegration[0]

    }

}


export default GlobalHelper


// WEBPACK FOOTER //
// ./src/common/GlobalHelper.jsx